import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';

import { ValidationResultItem } from '../../../typings/common';
import { EditPageBackToFrontErrorMapper } from '../../EditPageBackToFrontErrorMapper/EditPageBackToFrontErrorMapper';
import {
    createErrorsMapper,
    ErrorsMatcher
} from '../../ErrorsMapper/ErrorsMapper';

import * as keyset from './CampaignsEditBackErrorMapper.i18n';

const INDEX = '__INDEX__';

export const createCampaignsEditErrorMapper = (
    campaignsIds: Array<string>
): EditPageBackToFrontErrorMapper =>
    new EditPageBackToFrontErrorMapper(
        'campaigns',
        {
            campaignUpdateItems: [
                {
                    serverPath: ['name'],
                    clientPath: ['name']
                },
                {
                    serverPath: ['metrikaCounters', INDEX, 'id'],
                    clientPath: ['metrikaCounters']
                },
                {
                    serverPath: ['disabledPlaces', INDEX],
                    clientPath: ['bannedDomains']
                },
                {
                    serverPath: ['disabledIps', INDEX],
                    clientPath: ['disabledIps']
                },
                {
                    serverPath: ['notification', 'emailSettings', 'email'],
                    clientPath: ['notifications', 'common', 'email']
                },
                {
                    serverPath: ['contentLanguage'],
                    clientPath: ['bannersLanguage']
                },
                {
                    serverPath: ['vcard', 'companyName'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'companyName'
                    ]
                },
                {
                    serverPath: ['vcard', 'ogrn'],
                    clientPath: ['visitingCard', 'vcardAddItems', INDEX, 'ogrn']
                },
                {
                    serverPath: ['vcard', 'email'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'email'
                    ]
                },
                {
                    serverPath: ['vcard', 'country'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'address',
                        'country'
                    ]
                },
                {
                    serverPath: ['vcard', 'city'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'address',
                        'city'
                    ]
                },
                {
                    serverPath: ['vcard', 'street'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'address',
                        'street'
                    ]
                },
                {
                    serverPath: ['vcard', 'houseWithBuilding'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'address',
                        'houseWithBuilding'
                    ]
                },
                {
                    serverPath: ['vcard', 'apartment'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'address',
                        'apartment'
                    ]
                },
                {
                    serverPath: ['vcard', 'phone', 'countryCode'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'phone',
                        'countryCode'
                    ]
                },
                {
                    serverPath: ['vcard', 'phone', 'cityCode'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'phone',
                        'cityCode'
                    ]
                },
                {
                    serverPath: ['vcard', 'phone', 'phoneNumber'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'phone',
                        'phoneNumber'
                    ]
                },
                {
                    serverPath: ['vcard', 'phone', 'extension'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'phone',
                        'extension'
                    ]
                },
                {
                    serverPath: ['vcard', 'instantMessenger', 'login'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'instantMessenger',
                        'login'
                    ]
                },
                {
                    serverPath: ['vcard', 'extraMessage'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'extraMessage'
                    ]
                },
                {
                    serverPath: ['vcard', 'contactPerson'],
                    clientPath: [
                        'visitingCard',
                        'vcardAddItems',
                        INDEX,
                        'contactPerson'
                    ]
                }
            ]
        },
        campaignsIds,
        campaignsEditErrorTextMapper,
        INDEX
    );

const i18n = i18nFactory(keyset);

export const campaignsEditErrorTextMapper = createErrorsMapper<{}>(
    ({ idx }): Array<ErrorsMatcher<{}>> => [
        {
            path: ['campaigns', idx],
            codes: [
                {
                    code: 'DefectIds.METRIKA_RETURNS_RESULT_WITH_ERRORS',
                    message: (err: ValidationResultItem): string => {
                        return i18n('metrika-error');
                    }
                }
            ]
        },

        {
            path: ['campaigns', idx, 'metrikaCounters'],
            codes: [
                {
                    code:
                        'CampaignDefectIds.Gen.METRIKA_COUNTER_IS_UNAVAILABLE',
                    message: (err: ValidationResultItem): string => {
                        return i18n('metrika-counter-is-unavailable');
                    }
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'companyName'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-name-no-empty')
                },
                {
                    code:
                        'DefectIds.MUST_CONTAIN_LETTERS_OR_DIGITS_OR_PUNCTUATIONS',
                    message: (): string => i18n('vcard-name-letters')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-name-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'ogrn'
            ],
            codes: [
                {
                    code: 'OgrnConstraint.VoidDefectIds.INVALID_OGRN',
                    message: (): string => i18n('vcard-ogrn-format')
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-ogrn-no-empty')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'email'
            ],
            codes: [
                {
                    code:
                        'AddVcardValidationService.VoidDefectIds.CONTACT_EMAIL_INVALID_FORMAT',
                    message: (): string => i18n('vcard-mail-format')
                },
                {
                    code: 'VoidDefectIds.CONTACT_EMAIL_IS_EMPTY',
                    message: (): string => i18n('vcard-mail-no-empty')
                },
                {
                    code: 'VoidDefectIds.CONTACT_EMAIL_INVALID_FORMAT',
                    message: (): string => i18n('vcard-mail-format')
                },
                {
                    code:
                        'AddVcardValidationService.StringLengthDefectIds.CONTACT_EMAIL_TOO_LONG',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-mail-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'address',
                'country'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-country-no-empty')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-country-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'DefectIds.MUST_CONTAIN_LETTERS',
                    message: (): string => i18n('vcard-country-letters')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'address',
                'city'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-city-no-empty')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-city-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code: 'DefectIds.MUST_CONTAIN_LETTERS',
                    message: (): string => i18n('vcard-city-letters')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'address',
                'street'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-street-no-empty')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-street-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'DefectIds.MUST_CONTAIN_LETTERS_OR_DIGITS_OR_PUNCTUATIONS',
                    message: (): string => i18n('vcard-street-letters')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'address',
                'houseWithBuilding'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-house-no-empty')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-house-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'address',
                'apartment'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-office-no-empty')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-office-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'DefectIds.MUST_CONTAIN_LETTERS_OR_DIGITS_OR_PUNCTUATIONS',
                    message: (): string => i18n('vcard-office-format')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'phone',
                'countryCode'
            ],
            codes: [
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.COUNTRY_CODE_IS_EMPTY',
                    message: (): string =>
                        i18n('vcard-phone-country-code-no-empty')
                },
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.COUNTRY_CODE_IS_TOO_LONG',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-phone-country-code-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'PhoneValidator.VoidDefectIds.COUNTRY_CODE_FORMAT_IS_INVALID',
                    message: (): string =>
                        i18n('vcard-phone-country-code-format')
                },
                {
                    code:
                        'PhoneValidator.VoidDefectIds.COUNTRY_CODE_MUST_START_WITH_PLUS',
                    message: (): string =>
                        i18n('vcard-phone-country-code-start-plus')
                },
                {
                    code:
                        'PhoneValidator.CityCodeDefectIds.COUNTRY_CODE_MUST_NOT_START_WITH_PLUS',
                    message: (): string =>
                        i18n('vcard-phone-country-code-start-no-plus')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'phone',
                'cityCode'
            ],
            codes: [
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.CITY_CODE_IS_EMPTY',
                    message: (): string =>
                        i18n('vcard-phone-city-code-no-empty')
                },
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.CITY_CODE_IS_TOO_LONG',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-phone-city-code-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'PhoneValidator.VoidDefectIds.CITY_CODE_FORMAT_IS_INVALID',
                    message: (): string => i18n('vcard-phone-city-code-format')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'phone',
                'phoneNumber'
            ],
            codes: [
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.PHONE_NUMBER_IS_EMPTY',
                    message: (): string => i18n('vcard-phone-number-no-empty')
                },
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.PHONE_NUMBER_IS_TOO_SHORT',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-phone-number-min-length', {
                            minLength: get(err, 'params.minLength', '')
                        });
                    }
                },
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.PHONE_NUMBER_IS_TOO_LONG',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-phone-number-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'PhoneValidator.VoidDefectIds.PHONE_NUMBER_FORMAT_IS_INVALID',
                    message: (): string => i18n('vcard-phone-number-format')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'phone',
                'extension'
            ],
            codes: [
                {
                    code:
                        'PhoneValidator.VoidDefectIds.EXTENSION_FORMAT_IS_INVALID',
                    message: (): string => i18n('vcard-phone-extension-format')
                },
                {
                    code:
                        'PhoneValidator.StringLengthDefectIds.EXTENSION_IS_TOO_LONG',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-phone-extension-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'instantMessenger',
                'login'
            ],
            codes: [
                {
                    code:
                        'InstantMessengerValidator.StringDefectIds.LOGIN_IS_TOO_LONG',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-messenger-login-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'InstantMessengerValidator.VoidDefectIds.INVALID_ICQ_LOGIN_FORMAT',
                    message: (): string => i18n('vcard-messenger-icq-format')
                },
                {
                    code:
                        'InstantMessengerValidator.VoidDefectIds.INVALID_JABBER_LOGIN_FORMAT',
                    message: (): string => i18n('vcard-messenger-jabber-format')
                },
                {
                    code:
                        'InstantMessengerValidator.VoidDefectIds.INVALID_MAIL_AGENT_LOGIN_FORMAT',
                    message: (): string =>
                        i18n('vcard-messenger-mailruagent-format')
                },
                {
                    code:
                        'InstantMessengerValidator.VoidDefectIds.INVALID_SKYPE_OR_MSN_LOGIN_FORMAT',
                    message: (): string =>
                        i18n('vcard-messenger-skype-or-msn-format')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'extraMessage'
            ],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('vcard-descr-no-empty')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-descr-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                },
                {
                    code:
                        'DefectIds.MUST_CONTAIN_LETTERS_OR_DIGITS_OR_PUNCTUATIONS',
                    message: (): string => i18n('vcard-descr-format')
                }
            ]
        },

        {
            path: [
                'campaigns',
                idx,
                'visitingCard',
                'vcardAddItems',
                idx,
                'contactPerson'
            ],
            codes: [
                {
                    code:
                        'DefectIds.MUST_CONTAIN_LETTERS_OR_DIGITS_OR_PUNCTUATIONS',
                    message: (): string => i18n('vcard-name-letters')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('vcard-name-max-length', {
                            maxLength: get(err, 'params.maxLength', '')
                        });
                    }
                }
            ]
        },

        {
            path: ['campaigns', idx, 'name'],
            codes: [
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-max-length', {
                            count: get(err.params, 'maxLength')
                        });
                    }
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-cannot-be-null');
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-cannot-be-null');
                    }
                },
                {
                    code:
                        'DefectIds.MUST_CONTAIN_LETTERS_OR_DIGITS_OR_PUNCTUATIONS',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-enabled-symbols');
                    }
                }
            ]
        },

        {
            path: ['campaigns', idx, 'bannersLanguage'],
            codes: [
                {
                    code: 'CampaignDefectIds.ContentLanguage.BAD_LANGUAGE',
                    message: (err: ValidationResultItem): string => {
                        return i18n('banners-language-geo-error');
                    }
                }
            ]
        },

        {
            path: ['campaigns', idx, 'bannedDomains'],
            codes: [
                {
                    code: 'CampaignDefectIds.Places.INVALID_DOMAIN_OR_SSP',
                    message: (err: ValidationResultItem): string => {
                        return i18n('invalid-domain-or-ssp', {
                            subset:
                                err &&
                                err.params &&
                                typeof err.params !== 'object'
                                    ? err.params
                                    : ''
                        });
                    }
                },
                {
                    code: 'CampaignDefectIds.Places.UNKNOWN_SSP',
                    message: (err: ValidationResultItem): string => {
                        return i18n('unknown-ssp', {
                            subset:
                                err &&
                                err.params &&
                                typeof err.params !== 'object'
                                    ? err.params
                                    : ''
                        });
                    }
                }
            ]
        },

        {
            path: ['campaigns', idx, 'disabledIps'],
            codes: [
                {
                    code:
                        'CampaignDefectIds.Gen.IP_CANT_BE_FROM_PRIVATE_NETWORK',
                    message: (err: ValidationResultItem): string => {
                        return i18n('ip-from-private-network');
                    }
                },
                {
                    code:
                        'CampaignDefectIds.Gen.IP_CANT_BE_FROM_INTERNAL_NETWORK',
                    message: (err: ValidationResultItem): string => {
                        return i18n('ip-from-yandex-network');
                    }
                }
            ]
        },

        {
            path: ['campaigns', idx, 'notifications', 'common', 'email'],
            codes: [
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (err: ValidationResultItem): string => {
                        return i18n('email-error');
                    }
                }
            ]
        }
    ],
    error => i18n('default-error', { codeError: error.code })
);
